
// client/src/components/creditAssignment/AdminCreditAssignmentCompanies.js
//
// Admin: elenco aziende abilitate alla cessione del credito + aggiunta di una
// nuova azienda cercando tra le organization (company) presenti a sistema.

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Plus } from 'lucide-react';
import {
  fetchEnabledCompanies,
  searchAvailableCompanies,
  enableCompany
} from './creditAssignmentApi';

const AdminCreditAssignmentCompanies = () => {
  const { t } = useTranslation();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState('');
  const debounceRef = useRef(null);

  const loadCompanies = () => {
    setLoading(true);
    fetchEnabledCompanies()
      .then(setCompanies)
      .catch(() => setError(t('creditAssignment.errors.fetchCompanies')))
      .finally(() => setLoading(false));
  };

  useEffect(loadCompanies, [t]);

  useEffect(() => {
    if (!showAdd) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      searchAvailableCompanies(search)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, showAdd]);

  const handleEnable = async (organizationId) => {
    setAdding(organizationId);
    setError('');
    try {
      await enableCompany(organizationId);
      setResults((prev) => prev.filter((o) => o._id !== organizationId));
      loadCompanies();
    } catch (err) {
      setError(err.response?.data?.message || t('creditAssignment.errors.enableCompany'));
    } finally {
      setAdding('');
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="company-dashboard">
      <div className="card">
        <div className="card-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Building2 className="card-title-icon" size={20} />
            <span>{t('creditAssignment.admin.companiesTitle')}</span>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowAdd((v) => !v)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <Plus size={14} />
            {t('creditAssignment.admin.addCompany')}
          </button>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {showAdd && (
          <div style={{ margin: '16px 0', padding: '16px', background: '#f7f9fc', borderRadius: '8px' }}>
            <div className="form-group">
              <label htmlFor="ca-org-search">{t('creditAssignment.admin.searchCompany')}</label>
              <input
                id="ca-org-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('creditAssignment.admin.searchPlaceholder')}
              />
            </div>
            {searching ? (
              <p style={{ color: '#6c757d' }}>{t('common.loading')}</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>{t('creditAssignment.admin.orgName')}</th>
                    <th>{t('creditAssignment.admin.vatNumber')}</th>
                    <th>{t('common.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.length > 0 ? (
                    results.map((o) => (
                      <tr key={o._id}>
                        <td>{o.name}</td>
                        <td>{o.vatNumber || '-'}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={adding === o._id}
                            onClick={() => handleEnable(o._id)}
                          >
                            {adding === o._id ? t('common.loading') : t('creditAssignment.admin.enable')}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3">{t('creditAssignment.admin.noOrgResults')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        <table>
          <thead>
            <tr>
              <th>{t('creditAssignment.admin.orgName')}</th>
              <th>{t('creditAssignment.admin.vatNumber')}</th>
              <th>{t('creditAssignment.admin.clientCount')}</th>
              <th>{t('creditAssignment.admin.requestCount')}</th>
              <th>{t('common.action')}</th>
            </tr>
          </thead>
          <tbody>
            {companies.length > 0 ? (
              companies.map((c) => (
                <tr key={c.id}>
                  <td>{c.company?.name || '-'}</td>
                  <td>{c.company?.vatNumber || '-'}</td>
                  <td>{c.clientCount}</td>
                  <td>{c.requestCount}</td>
                  <td>
                    <Link
                      to={`/admin/credit-assignment/companies/${c.company?._id || c.company}`}
                      className="view-btn"
                    >
                      {t('common.view')}
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">{t('creditAssignment.admin.noCompanies')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCreditAssignmentCompanies;
